import { BasePlugin } from "@uppy/core"
import { DirectUpload } from "@rails/activestorage"

/**
 * Uppy v5 plugin for Rails ActiveStorage direct uploads.
 *
 * Replaces the abandoned `uppy-activestorage-upload` package (v0.1.0)
 * which only supports Uppy v0.27.x.
 *
 * Usage:
 *   uppy.use(ActiveStorageUpload, {
 *     directUploadUrl: "/rails/active_storage/direct_uploads"
 *   })
 *
 * After upload, each file's `response` will contain `{ signed_id }`.
 */
export default class ActiveStorageUpload extends BasePlugin {
  constructor(uppy, opts) {
    super(uppy, opts)
    this.id = opts.id || "ActiveStorageUpload"
    this.type = "uploader"

    this.opts = {
      directUploadUrl: null,
      timeout: 30 * 1000,
      ...opts,
    }

    this.handleUpload = this.handleUpload.bind(this)
  }

  install() {
    this.uppy.addUploader(this.handleUpload)
  }

  uninstall() {
    this.uppy.removeUploader(this.handleUpload)
  }

  handleUpload(fileIDs) {
    if (fileIDs.length === 0) return Promise.resolve()

    const files = fileIDs.map((id) => this.uppy.getFile(id))
    const promises = files.map((file) => this.uploadFile(file))

    return Promise.allSettled(promises)
  }

  uploadFile(file) {
    return new Promise((resolve, reject) => {
      const delegate = {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              this.uppy.emit("upload-progress", file, {
                uploader: this,
                bytesUploaded: event.loaded,
                bytesTotal: event.total,
              })
            }
          })
        },
      }

      const upload = new DirectUpload(
        file.data,
        this.opts.directUploadUrl,
        delegate
      )

      this.uppy.emit("upload-started", file)

      upload.create((error, blob) => {
        if (error) {
          this.uppy.emit("upload-error", file, error)
          return reject(error)
        }

        const response = { signed_id: blob.signed_id }
        this.uppy.setFileState(file.id, { response })
        this.uppy.emit("upload-success", file, response)
        resolve(file)
      })
    })
  }
}
